import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCreateBien, useUpdateBien, useBiens, useBien } from "@/hooks/useBiens";
import { useProfiles } from "@/hooks/useProfiles";
import { BienUpdateSchema } from "@/schemas/bien";
import { Copy, FileUp, ImagePlus, Plus, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { buildDuplicateDraft, copyBienPhotos, stripTransactionSpecific } from "@/v2/lib/duplicateBien";
import type { BienPhoto, Proximite, SurfaceOrientation } from "@/types/bien";
import { useToast } from "@/components/ui/Toast";
import { FormField } from "@/components/ui/FormField";
import { SelectField } from "@/components/ui/SelectField";
import { TextAreaField } from "@/components/ui/TextAreaField";
import { DateField } from "@/components/ui/DateField";
import { FreeTextAutocompleteField } from "@/components/ui/FreeTextAutocompleteField";
import { Checkbox } from "@/components/ui/Checkbox";
import { PhotoGallery } from "@/components/biens/PhotoGallery";
import { PebPdfDropzone } from "@/components/biens/PebPdfDropzone";
import {
  VENTE_SECTIONS,
  VENTE_DETAILS_LANGS,
  EQUIPEMENT_TAGS,
  VENTE_TITLE_MAX,
  VENTE_DESCRIPTION_MAX,
  PROXIMITE_POIS,
  getVenteOptions,
  getVenteFormFields,
  getVenteMissingHints,
  suggestPrixM2,
  venteDispoDatePatch,
} from "@/v2/lib/venteFormFields";
import type { VenteFieldDef } from "@/v2/lib/venteFormFields";
import {
  getCityFromPostalCode,
  getPostalCodeFromCity,
  getRegionFromPostalCode,
} from "@/lib/postalCodeLookup";
import type { BienDraft } from "@/lib/bienPrepValidation";
import { cn } from "@/lib/utils";
import { buildRepeaterPatch } from "@/lib/surfaceOrientationRows";

// Brouillon vente : transaction figée à "vente" (création contextuelle — le
// choix Location/Vente est fait EN AMONT, pas de modale ici) et disponibilite
// "EN PRÉPARATION" (= brouillon, active le surlignage des requis). Un bien en
// vente n'a jamais de cycle de sortie : il n'a pas besoin d'être classé pour
// ça (plan « deux cycles », 2026-09-15 — `entreeGestion` n'est plus lu).
type Draft = BienDraft & { disponibilite: string };
const INITIAL_DRAFT: Draft = {
  transaction: "vente",
  disponibilite: "EN PRÉPARATION",
};

/**
 * Formulaire de CRÉATION d'un bien VENTE (route /v2/biens-vente/new).
 *
 * Même pattern que BienCreateFormPage (location) : brouillon local →
 * useCreateBien() au clic « Créer » → autosave useUpdateBien(createdId) au
 * blur. Les sections/champs viennent de la config partagée venteFormFields.ts
 * (single source of truth avec BienVenteDetailPage).
 *
 * Duplication : accepte location.state.duplicateFromId (comme la page
 * location) ; le brouillon est passé par stripTransactionSpecific(…, "vente")
 * pour vider les champs location (loyers, charges, renon…).
 */
export function BienVenteCreateFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [data, setData] = useState<Draft>(INITIAL_DRAFT);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [hasEdited, setHasEdited] = useState(false);
  /** Dernier brouillon refusé par la base — garde-fou anti-boucle de l'autosave. */
  const lastRejectedData = useRef<Draft | null>(null);

  // Duplication (C-Level) : id du bien source via le state de navigation
  // (posé par V2BienDetailWrapper ou par la modale de BienCreateFormPage
  // quand l'utilisateur choisit "vente").
  const duplicateFromId = (location.state as { duplicateFromId?: string } | null)?.duplicateFromId;
  const { data: source } = useBien(duplicateFromId);
  const [dupInit, setDupInit] = useState(false);
  const [dupPhotos, setDupPhotos] = useState<BienPhoto[]>([]);
  const [dupRef, setDupRef] = useState<string>("");
  useEffect(() => {
    if (!duplicateFromId || dupInit || !source) return;
    setData({
      ...(stripTransactionSpecific(buildDuplicateDraft(source), "vente") as Draft),
      transaction: "vente",
      disponibilite: "EN PRÉPARATION",
    });
    setDupPhotos((source.photos as BienPhoto[] | null) ?? []);
    setDupRef(source.referenceInterne || source.reference || source.adresse || "");
    setDupInit(true);
  }, [duplicateFromId, source, dupInit]);

  const createBien = useCreateBien();
  const updateBien = useUpdateBien(createdId ?? "");

  // Options traduites (config partagée)
  const OPTS = useMemo(() => getVenteOptions(t), [t]);

  // Suggestions projet (valeurs distinctes sur d'autres biens) — comme la location
  const { data: allBiens = [] } = useBiens();
  const projetSuggestions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const b of allBiens) {
      const v = (b.projet ?? "").trim();
      if (!v) continue;
      const key = v.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(v);
    }
    return out.sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
  }, [allBiens]);

  // Collaborateurs SRS (rôle collaborateur, actifs) — mêmes options que la
  // fiche location (retour user 2026-07-13 : même déroulant, même intitulé).
  const { data: profiles = [] } = useProfiles();
  const collaborateurOptions = useMemo(
    () =>
      profiles
        .filter((p) => p.role === "collaborateur" && p.statut !== "inactif")
        .map((p) => ({
          value: p.id,
          label: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email,
        })),
    [profiles],
  );

  // Requis vente (adresse, codePostal, ville, typeBien, prix) → bordure rouge
  const hints = useMemo(() => getVenteMissingHints(data), [data]);
  // sousOption exclue à la CRÉATION : un bien neuf n'est jamais sous option
  // (la case ne vit que sur la fiche BienVenteDetailPage ; guard DB 087).
  const fields = useMemo(
    () => getVenteFormFields(data).filter((f) => f.key !== "sousOption"),
    [data],
  );
  const prixM2Suggestion = useMemo(
    () => suggestPrixM2(data.prix, data.surfaceHabitable),
    [data.prix, data.surfaceHabitable],
  );

  // ── Setters ────────────────────────────────────────────────────────────────
  const set = (key: string) => (value: string) => {
    setHasEdited(true);
    setData((prev) => ({ ...prev, [key]: value }));
  };
  // Date de disponibilité ⇒ déroulant « À une date précise » (cf. venteDispoDatePatch).
  const setDispoDate = (value: string) => {
    setHasEdited(true);
    setData((prev) => ({ ...prev, ...venteDispoDatePatch(value) }));
  };

  const setBoolFn = (key: string) => (s: string) => {
    setHasEdited(true);
    setData((prev) => ({
      ...prev,
      [key]: s === "oui" ? true : s === "non" ? false : null,
    }));
  };

  const boolToOption = (v: unknown): string =>
    v === true ? "oui" : v === false ? "non" : "";

  const strVal = (k: string): string => {
    const v = data[k];
    return typeof v === "string" ? v : "";
  };

  // Postal code lookup — même comportement que BienCreateFormPage
  const handleCodePostalChange = (value: string) => {
    setHasEdited(true);
    setData((prev) => {
      const updated: Draft = { ...prev, codePostal: value };
      const city = getCityFromPostalCode(value);
      const region = getRegionFromPostalCode(value);
      if (city) updated.ville = city;
      if (region) updated.region = region;
      return updated;
    });
  };

  const handleVilleChange = (value: string) => {
    setHasEdited(true);
    setData((prev) => {
      const updated: Draft = { ...prev, ville: value };
      const code = getPostalCodeFromCity(value);
      if (code) {
        updated.codePostal = code;
        const region = getRegionFromPostalCode(code);
        if (region) updated.region = region;
      }
      return updated;
    });
  };

  // ── Repeaters terrasses / chambres ────────────────────────────────────────
  const rowsOf = (key: "terrasses" | "chambresDetail"): SurfaceOrientation[] => {
    const v = data[key];
    return Array.isArray(v) ? (v as SurfaceOrientation[]) : [];
  };

  // Miroirs (surfaceTerrasse, nbChambres, surfacesChambres) centralisés dans
  // `buildRepeaterPatch` — même logique que la fiche vente et la fiche location.
  // Corrige au passage l'absence de miroir `surfacesChambres` : le répéteur
  // n'alimentait que `chambresDetail`, or c'est `surfacesChambres` que lit le
  // mapper Immoweb — les surfaces d'un bien en vente n'y arrivaient jamais.
  const setRows = (key: "terrasses" | "chambresDetail") => (next: SurfaceOrientation[]) => {
    setHasEdited(true);
    setData((prev) => ({ ...prev, ...buildRepeaterPatch(key, next) }) as Draft);
  };

  const renderRepeater = (key: "terrasses" | "chambresDetail") => {
    const isTerrasse = key === "terrasses";
    const rows = rowsOf(key);
    const update = setRows(key);
    const surfaceLabelKey = isTerrasse ? "vente.form.terrasseSurface" : "vente.form.chambreSurface";
    const orientationLabelKey = isTerrasse ? "vente.form.terrasseOrientation" : "vente.form.chambreOrientation";
    return (
      <div key={key} className="sm:col-span-2 flex flex-col gap-3">
        {rows.map((row, i) => (
          <div key={`${key}-${i}`} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <FormField
              label={t(surfaceLabelKey, { index: i + 1 })}
              value={row.surface}
              onChange={(v) => update(rows.map((r, j) => (j === i ? { ...r, surface: v } : r)))}
            />
            <SelectField
              label={t(orientationLabelKey, { index: i + 1 })}
              value={row.orientation}
              onChange={(v) => update(rows.map((r, j) => (j === i ? { ...r, orientation: v } : r)))}
              options={OPTS.orientation}
            />
            <button
              type="button"
              onClick={() => update(rows.filter((_, j) => j !== i))}
              className="p-2 mb-0.5 rounded-md text-text-muted hover:text-danger hover:bg-danger/10 transition-colors cursor-pointer outline-none shadow-none"
              aria-label={t("common.delete")}
              title={t("common.delete")}
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => update([...rows, { surface: "", orientation: "" }])}
          className="self-start flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-text-secondary border border-dashed border-border rounded-md hover:border-accent hover:text-accent bg-surface transition-colors cursor-pointer outline-none shadow-none"
        >
          <Plus size={15} />
          {t(isTerrasse ? "vente.form.addTerrasse" : "vente.form.addChambre")}
        </button>
      </div>
    );
  };

  // ── Équipements (tags EXACTS de EQUIPEMENT_RULES) ─────────────────────────
  const renderEquipements = () => {
    const current = Array.isArray(data.equipements) ? (data.equipements as string[]) : [];
    const toggleTag = (tag: string) => {
      setHasEdited(true);
      setData((prev) => {
        const cur = Array.isArray(prev.equipements) ? (prev.equipements as string[]) : [];
        return {
          ...prev,
          equipements: cur.includes(tag) ? cur.filter((x) => x !== tag) : [...cur, tag],
        } as Draft;
      });
    };
    return (
      <div key="equipements" className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {EQUIPEMENT_TAGS.map(({ tag, labelKey }) => (
          <Checkbox
            key={tag}
            label={t(labelKey)}
            checked={current.includes(tag)}
            onChange={() => toggleTag(tag)}
          />
        ))}
      </div>
    );
  };

  // ── À proximité (JSONB proximite) ─────────────────────────────────────────
  const renderProximite = () => {
    const prox = (data.proximite ?? {}) as Proximite;
    const setEntry = (poi: string, patch: Partial<{ present: string; distance: string }>) => {
      setHasEdited(true);
      setData((prev) => {
        const cur = (prev.proximite ?? {}) as Proximite;
        const entry = cur[poi as keyof Proximite] ?? { present: "", distance: "" };
        return { ...prev, proximite: { ...cur, [poi]: { ...entry, ...patch } } } as Draft;
      });
    };
    return (
      <div key="proximite" className="sm:col-span-2 flex flex-col gap-4">
        {PROXIMITE_POIS.map((poi) => {
          const entry = prox[poi] ?? { present: "", distance: "" };
          return (
            <div key={poi} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SelectField
                label={t(`vente.fields.proximite.${poi}`)}
                value={entry.present}
                onChange={(v) => setEntry(poi, { present: v })}
                options={OPTS.ouiNon}
              />
              <FormField
                label={t("vente.fields.proximiteDistance")}
                value={entry.distance}
                onChange={(v) => setEntry(poi, { distance: v })}
                placeholder={t("vente.form.distancePlaceholder")}
              />
            </div>
          );
        })}
      </div>
    );
  };

  // ── Galerie photos ─────────────────────────────────────────────────────────
  // L'upload a besoin d'un bienId (chemin storage) → galerie active seulement
  // APRÈS le premier « Créer le bien » (createdId). Avant : hint discret.
  // La colonne `photos` est persistée immédiatement après chaque upload (les
  // fichiers sont déjà dans le storage — pas d'attente du blur).
  const renderPhotos = () => {
    if (!createdId) {
      return (
        <div
          key="photos"
          className="sm:col-span-2 flex items-center gap-2.5 px-3 py-4 rounded-md border border-dashed border-border bg-surface text-sm text-text-muted"
        >
          <ImagePlus size={16} className="flex-shrink-0" />
          {t("vente.form.photosAfterCreate")}
        </div>
      );
    }
    const current = Array.isArray(data.photos) ? (data.photos as BienPhoto[]) : [];
    return (
      <div key="photos" className="sm:col-span-2">
        <PhotoGallery
          bienId={createdId}
          value={current}
          onChange={(next) => {
            setData((prev) => ({ ...prev, photos: next } as Draft));
            setHasEdited(false);
            updateBien.mutate({ ...buildPatch(), photos: next });
          }}
        />
      </div>
    );
  };

  // ── PDF certificat PEB ─────────────────────────────────────────────────────
  // Même contrainte que la galerie : l'upload a besoin d'un bienId (chemin
  // storage) → dropzone active seulement APRÈS le premier « Créer le bien ».
  // La colonne pebCertificatPdf est persistée immédiatement après le dépôt.
  const renderPebPdf = () => {
    if (!createdId) {
      return (
        <div
          key="pebCertificatPdf"
          className="sm:col-span-2 flex items-center gap-2.5 px-3 py-4 rounded-md border border-dashed border-border bg-surface text-sm text-text-muted"
        >
          <FileUp size={16} className="flex-shrink-0" />
          {t("vente.form.pebPdfAfterCreate")}
        </div>
      );
    }
    return (
      <div key="pebCertificatPdf" className="sm:col-span-2">
        <PebPdfDropzone
          bienId={createdId}
          value={strVal("pebCertificatPdf")}
          onChange={(url) => {
            setData((prev) => ({ ...prev, pebCertificatPdf: url } as Draft));
            setHasEdited(false);
            updateBien.mutate({ ...buildPatch(), pebCertificatPdf: url });
          }}
        />
      </div>
    );
  };

  // ── Détails multilingues (3 cartes FR/NL/EN) ──────────────────────────────
  const renderDetailsSection = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      {VENTE_DETAILS_LANGS.map(({ titreKey, descriptionKey, cardLabelKey }) => (
        <div key={titreKey} className="bg-surface border border-border rounded-lg p-5">
          <h3 className="text-xs font-semibold tracking-widest uppercase text-text-secondary mb-4">
            {t(cardLabelKey)}
          </h3>
          <div className="flex flex-col gap-4">
            <FormField
              label={t("common.titleField")}
              value={strVal(titreKey)}
              onChange={set(titreKey)}
              maxLength={VENTE_TITLE_MAX}
            />
            <TextAreaField
              label={t("common.description")}
              value={strVal(descriptionKey)}
              onChange={set(descriptionKey)}
              maxLength={VENTE_DESCRIPTION_MAX}
            />
          </div>
        </div>
      ))}
    </div>
  );

  // ── Rendu d'un champ depuis sa def (config partagée) ──────────────────────
  const renderField = (def: VenteFieldDef) => {
    const hint = hints.get(def.key);
    const spanCls = def.span2 ? "sm:col-span-2" : undefined;
    switch (def.kind) {
      case "chambres":
        return renderRepeater("chambresDetail");
      case "terrasses":
        return renderRepeater("terrasses");
      case "equipements":
        return renderEquipements();
      case "proximite":
        return renderProximite();
      case "photos":
        return renderPhotos();
      case "pebPdf":
        return renderPebPdf();
      case "collabSrs":
        return (
          <SelectField
            key={def.key}
            className={spanCls}
            label={t(def.labelKey ?? def.key)}
            value={strVal(def.key)}
            onChange={set(def.key)}
            options={collaborateurOptions}
          />
        );
      case "freetext":
        return (
          <FreeTextAutocompleteField
            key={def.key}
            className={spanCls}
            label={t(def.labelKey ?? def.key)}
            value={strVal(def.key)}
            onChange={set(def.key)}
            suggestions={projetSuggestions}
            missingHint={hint}
          />
        );
      case "select":
        return (
          <SelectField
            key={def.key}
            className={spanCls}
            label={t(def.labelKey ?? def.key)}
            value={strVal(def.key)}
            onChange={set(def.key)}
            options={def.optionsId ? OPTS[def.optionsId] : []}
            missingHint={hint}
          />
        );
      case "boolSelect":
        return (
          <SelectField
            key={def.key}
            className={spanCls}
            label={t(def.labelKey ?? def.key)}
            value={boolToOption(data[def.key])}
            onChange={setBoolFn(def.key)}
            options={OPTS.ouiNon}
            missingHint={hint}
          />
        );
      case "date":
        return (
          <DateField
            key={def.key}
            className={spanCls}
            label={t(def.labelKey ?? def.key)}
            value={strVal(def.key)}
            onChange={def.key === "venteDisponibiliteDate" ? setDispoDate : set(def.key)}
            missingHint={hint}
          />
        );
      case "textarea":
        return (
          <TextAreaField
            key={def.key}
            className={spanCls ?? "sm:col-span-2"}
            label={t(def.labelKey ?? def.key)}
            value={strVal(def.key)}
            onChange={set(def.key)}
            maxLength={def.maxLength}
          />
        );
      case "checkbox":
        return (
          <div key={def.key} className={cn("flex items-end pb-2", spanCls)}>
            <Checkbox
              label={t(def.labelKey ?? def.key)}
              checked={!!data[def.key]}
              onChange={(c) => {
                setHasEdited(true);
                setData((prev) => ({ ...prev, [def.key]: c }));
              }}
            />
          </div>
        );
      case "text":
      default: {
        if (def.key === "codePostal") {
          return (
            <FormField
              key={def.key}
              className={spanCls}
              label={t(def.labelKey ?? def.key)}
              value={strVal("codePostal")}
              onChange={handleCodePostalChange}
              missingHint={hint}
            />
          );
        }
        if (def.key === "ville") {
          return (
            <FormField
              key={def.key}
              className={spanCls}
              label={t(def.labelKey ?? def.key)}
              value={strVal("ville")}
              onChange={handleVilleChange}
              missingHint={hint}
            />
          );
        }
        if (def.key === "prixM2") {
          // Suggestion auto = round(prix / surfaceHabitable) en placeholder ;
          // le champ reste librement éditable.
          return (
            <FormField
              key={def.key}
              className={spanCls}
              label={t(def.labelKey ?? def.key)}
              value={strVal("prixM2")}
              onChange={set("prixM2")}
              placeholder={prixM2Suggestion || undefined}
              missingHint={hint}
            />
          );
        }
        return (
          <FormField
            key={def.key}
            className={spanCls}
            label={t(def.labelKey ?? def.key)}
            value={strVal(def.key)}
            onChange={set(def.key)}
            maxLength={def.maxLength}
            missingHint={hint}
          />
        );
      }
    }
  };

  // ── Persistance ────────────────────────────────────────────────────────────
  // Patch Zod normalisé — même stratégie que la location : strip null→absent
  // (BienUpdateSchema a des .optional() non-nullable sur les champs communs ;
  // lors d'une duplication les colonnes DB vides arrivent en null JS).
  const buildPatch = () => {
    const patchInput: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      if (v !== null) patchInput[k] = v;
    }
    if (!patchInput.transaction) patchInput.transaction = "vente";
    return BienUpdateSchema.parse(patchInput);
  };

  // Autosave sur blur (après création seulement, si changements en attente)
  const handleBlur = () => {
    if (!createdId || !hasEdited || updateBien.isPending) return;
    updateBien.mutate(buildPatch(), { onSuccess: () => setHasEdited(false) });
  };

  /**
   * Auto-save DEBOUNCÉ, en plus du blur — même filet que la fiche vente.
   * Les widgets custom (SelectField, checkboxes, répéteurs) ne renvoient pas
   * de blur exploitable : cliquer une option la démonte aussitôt, et un
   * élément focusé retiré du DOM ne déclenche pas de `focusout`. Sans ça, un
   * changement de déroulant restait dans le brouillon sans jamais partir en
   * base tant qu'aucun champ texte n'était touché ensuite.
   */
  useEffect(() => {
    if (!createdId || !hasEdited || updateBien.isPending) return;
    // Anti-boucle : sans ça un patch refusé serait rejoué toutes les 1,5 s
    // (et toasté d'autant, cf. meta.toastOnError sur useUpdateBien).
    if (lastRejectedData.current === data) return;
    const timer = setTimeout(() => {
      const attempted = data;
      updateBien.mutate(buildPatch(), {
        onSuccess: () => setHasEdited(false),
        onError: () => {
          lastRejectedData.current = attempted;
        },
      });
    }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, createdId, hasEdited, updateBien.isPending]);

  // Création libre (les bordures rouges = guidage, pas de blocage — comme la location)
  const handleCreate = () => {
    if (createdId || createBien.isPending) return;
    const id = crypto.randomUUID();
    const patch = buildPatch();
    createBien.mutate({ id, ...patch }, {
      onSuccess: async () => {
        setCreatedId(id);
        setHasEdited(false);
        // Duplication : copie physique des photos du bien source (best-effort).
        if (dupPhotos.length > 0) {
          try {
            const newPhotos = await copyBienPhotos(dupPhotos, id);
            if (newPhotos.length > 0) {
              await supabase.from("biens").update({ photos: newPhotos }).eq("id", id);
            }
          } catch {
            // best-effort : le bien est créé même si la copie photo échoue
          }
        }
        toast.success(t("v2.bienCreate.createdToast"));
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : String(e)),
    });
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-text">{t("vente.form.title")}</h1>
        <p className="mt-1 text-sm text-text-muted">{t("vente.form.subtitle")}</p>
        {dupRef && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-accent/30 bg-accent/5 px-3 py-2 text-sm text-text-secondary">
            <Copy size={15} className="text-accent flex-shrink-0 mt-0.5" />
            <span>{t("v2.bienCreate.duplicateBanner", { ref: dupRef })}</span>
          </div>
        )}
      </div>

      {/* Sections (config partagée venteFormFields) */}
      <div className="space-y-5" onBlur={handleBlur}>
        {VENTE_SECTIONS.map((section) => {
          if (section === "details") {
            return <div key="details">{renderDetailsSection()}</div>;
          }
          const sectionFields = fields.filter((f) => f.section === section);
          if (sectionFields.length === 0) return null;
          return (
            <div key={section} className="bg-surface border border-border rounded-lg p-5">
              <h2 className="text-xs font-medium tracking-wider uppercase text-text-muted mb-4">
                {t(`vente.form.sections.${section}`)}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {sectionFields.map((f) => renderField(f))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="mt-6 flex items-center gap-4">
        {!createdId && (
          <button
            type="button"
            onClick={handleCreate}
            disabled={createBien.isPending}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-medium outline-none shadow-none transition-colors",
              "bg-accent text-white hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed",
            )}
          >
            {t("v2.bienCreate.createButton")}
          </button>
        )}
        {createdId && (
          <>
            <button
              type="button"
              onClick={() => navigate(`/v2/biens/${createdId}`)}
              className="text-sm text-accent underline underline-offset-2 outline-none shadow-none hover:opacity-80 transition-opacity cursor-pointer"
            >
              {t("v2.bienCreate.openFullSheet")}
            </button>
            <button
              type="button"
              onClick={() => navigate("/v2/biens-vente")}
              className="text-sm text-text-secondary underline underline-offset-2 outline-none shadow-none hover:text-accent transition-colors cursor-pointer"
            >
              {t("vente.form.backToCatalogue")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
